import { createRoute, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import {
  manageUserCreatePageQueryOptions,
  manageUserEditPageQueryOptions,
} from "~/app/api/pageData/manageUserFormPageQuery";
import type {
  ManageUserEditSearch,
  ManageUserFormPageInitialData,
} from "~/app/api/pageData/manageUserFormPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import { createEditUserAccountPage } from "~/pages/EditUserAccountPage/EditUserAccountPage";

const MANAGE_USERS_BASE_PATH = "/my-company/organization-management/manage-users";
const MANAGE_USER_EDIT_PATH = `${MANAGE_USERS_BASE_PATH}/edit`;
const MANAGE_USER_CREATE_PATH = `${MANAGE_USERS_BASE_PATH}/create`;

function ManageUserFormBody({ result }: { result: PageInitialData<ManageUserFormPageInitialData> }) {
  const shellProps = useStorefrontShellProps();
  const pageData = result.data?.reactPageProps;

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {pageData ? createEditUserAccountPage(pageData) : null}
    </StorefrontShellFrame>
  );
}

function ManageUserEditRoute() {
  const result = useLoaderData({
    from: MANAGE_USER_EDIT_PATH,
  }) as PageInitialData<ManageUserFormPageInitialData>;

  return <ManageUserFormBody result={result} />;
}

function ManageUserCreateRoute() {
  const result = useLoaderData({
    from: MANAGE_USER_CREATE_PATH,
  }) as PageInitialData<ManageUserFormPageInitialData>;

  return <ManageUserFormBody result={result} />;
}

export async function loadManageUserEditPageData(context: { queryClient: QueryClient }, search: ManageUserEditSearch) {
  const result = await context.queryClient.ensureQueryData(manageUserEditPageQueryOptions(search));

  return unwrapPageInitialData(result);
}

export async function loadManageUserCreatePageData(context: { queryClient: QueryClient }) {
  const result = await context.queryClient.ensureQueryData(manageUserCreatePageQueryOptions());

  return unwrapPageInitialData(result);
}

export const manageUserEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: MANAGE_USER_EDIT_PATH,
  validateSearch: (search: Record<string, unknown>): ManageUserEditSearch => ({
    user: typeof search.user === "string" ? search.user : undefined,
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps }) => loadManageUserEditPageData(context, deps),
  component: ManageUserEditRoute,
});

export const manageUserCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: MANAGE_USER_CREATE_PATH,
  loader: async ({ context }) => loadManageUserCreatePageData(context),
  component: ManageUserCreateRoute,
});
